import { logoutAction } from "@/features/auth/store/logoutAction";
import {
  IS_WHITE_LABELED_DOMAIN,
  IS_LOCAL_HOST,
  IS_PROD,
  IS_DEV,
  SITE_URL,
  WHITE_LABELED_DOMAIN_URL,
} from "@/config/appConfig";
import { REDIRECT_CONST } from "@/config/constants";
import store, { persistor } from "@/config/store";
import { authService } from "@/features/auth/services/authService";
import { reloadOtherTabs } from "./authHelper";
import { logger } from "@/helpers/loggerHelper";
import { getSubdomain } from "@/helpers/utils";

type TokenBrandData = {
  [key: string]: any;
  is_white_label?: boolean;
  subdomain?: string;
};
type LoginWithTokenArgs = {
  data: TokenBrandData;
};
type AccessTokenResponse = {
  data?: string;
  status?: number;
};

export const redirectToUrl = (url: string) => {
  // display a full screen loading indicator
  const loadingIndicator = document.createElement("div");
  loadingIndicator.style.position = "fixed";
  loadingIndicator.style.top = "0";
  loadingIndicator.style.left = "0";
  loadingIndicator.style.width = "100%";
  loadingIndicator.style.height = "100%";
  loadingIndicator.style.backgroundColor = "rgba(255, 255, 255, 1)";
  loadingIndicator.style.zIndex = "9999999999";
  loadingIndicator.style.display = "flex";
  loadingIndicator.style.justifyContent = "center";
  loadingIndicator.style.alignItems = "center";
  loadingIndicator.style.fontSize = "10px";
  loadingIndicator.style.fontWeight = "bold";
  loadingIndicator.style.color = "black";
  loadingIndicator.textContent = "Redirecting...";
  document.body.appendChild(loadingIndicator);

  window.location.replace(url);
};

const buildLoginRedirectUrl = (baseUrl: string, accessToken?: string) => {
  try {
    const redirectUrl = new URL(baseUrl);
    redirectUrl.pathname = "/login-redirect";
    redirectUrl.searchParams.set("token", accessToken);
    return redirectUrl.toString();
  } catch (error) {
    logger.error("Error building login redirect URL:", error);
    return "";
  }
};

const buildWhiteLabelSubdomainUrl = (subdomain?: string) => {
  try {
    const whiteLabelUrl = new URL(WHITE_LABELED_DOMAIN_URL);
    const hostParts = whiteLabelUrl.hostname.split(".");
    whiteLabelUrl.hostname =
      hostParts.length > 2
        ? [subdomain, ...hostParts.slice(1)].join(".")
        : `${subdomain}.${whiteLabelUrl.hostname}`;
    return whiteLabelUrl.origin;
  } catch (error) {
    logger.error("Error building white label subdomain URL:", error);
    return "";
  }
};

const buildLocalWhiteLabelSubdomainUrl = (subdomain?: string) => {
  try {
    const localUrl = new URL(SITE_URL);
    localUrl.hostname = `${subdomain}.${localUrl.hostname}`;
    return localUrl.origin;
  } catch (error) {
    logger.error("Error building local white label URL:", error);
    return "";
  }
};

export const logout = async () => {
  // return Promise.resolve();
  try {
    // Clear all data from localStorage (be cautious if you only want specific keys)
    localStorage.clear();

    // Optionally, clear sessionStorage if used for session data
    sessionStorage.clear();

    // Purge Redux persist store (clear persisted state)
    await persistor.purge();

    await store.dispatch(logoutAction());

    reloadOtherTabs();

    // Optionally log or notify the user
    (logger as any).log("Logged out successfully.");
  } catch (error) {
    logger.error("Error during logout process", error);
  }
};

export const loginWithToken = async ({ data }: LoginWithTokenArgs) => {
  // data is the selected brand
  const { is_white_label, subdomain } = data;

  try {
    // RP if white label is enabled for the selected brand, and the current url is also of white label, then return
    // also if the subdomain is same as the selected brand, then return
    // if (is_white_label && IS_WHITE_LABELED_DOMAIN) {

    const isWhiteLabel = IS_LOCAL_HOST
      ? is_white_label
      : is_white_label && IS_WHITE_LABELED_DOMAIN;

    if (isWhiteLabel) {
      const currentSubdomain = getSubdomain();

      if (currentSubdomain === subdomain) {
        return;
      }
    }

    if (IS_LOCAL_HOST) {
      await redirectForLocalHost({ is_white_label, subdomain });
    } else {
      if (is_white_label) {
        await redirectForWhiteLabel({ is_white_label, subdomain });
      } else {
        await redirectForHyper({ is_white_label, subdomain });
      }
    }

    return;
  } catch (error) {
    logger.error("Error setting token:", error);
    return;
  }
};

async function redirectForWhiteLabel({
  is_white_label,
  subdomain,
}: TokenBrandData) {
  // called for white label
  if (!is_white_label) {
    return;
  }

  const res = (await authService.getAccessToken()) as AccessTokenResponse;

  if (res.status === 200) {
    const { data } = res;
    const accessToken = data;

    // if already on that subdomain, then return
    if (IS_WHITE_LABELED_DOMAIN && getSubdomain() === subdomain) {
      return;
    }

    const redirectUrl = buildLoginRedirectUrl(
      buildWhiteLabelSubdomainUrl(subdomain),
      accessToken
    );

    if (redirectUrl) {
      await logout(); // logout the user before redirecting
      redirectToUrl(redirectUrl);
    }
  }
}

async function redirectForHyper(_data?: TokenBrandData) {
  // called for non white label

  const currentSubdomain = getSubdomain();
  if (
    REDIRECT_CONST.WHITE_LIST_SUBDOMAINS.includes(currentSubdomain) &&
    !IS_WHITE_LABELED_DOMAIN
  ) {
    return;
  }

  const res = (await authService.getAccessToken()) as AccessTokenResponse;

  if (res.status === 200) {
    const { data } = res;
    const accessToken = data;

    let redirectUrl = "";

    if (IS_PROD && !REDIRECT_CONST.PROD_SUBDOMAINS.includes(currentSubdomain)) {
      redirectUrl = buildLoginRedirectUrl(SITE_URL, accessToken);
    } else if (
      IS_DEV &&
      !REDIRECT_CONST.DEV_SUBDOMAINS.includes(currentSubdomain)
    ) {
      redirectUrl = buildLoginRedirectUrl(SITE_URL, accessToken);
    } else if (IS_WHITE_LABELED_DOMAIN) {
      redirectUrl = buildLoginRedirectUrl(SITE_URL, accessToken);
    }

    if (redirectUrl) {
      // if (IS_WHITE_LABELED_DOMAIN) {
      await logout(); // logout the user before redirecting
      // }
      redirectToUrl(redirectUrl);
    }
  }
}

async function redirectForLocalHost({
  is_white_label,
  subdomain,
}: TokenBrandData) {
  if (!is_white_label) {
    return;
  }

  const res = (await authService.getAccessToken()) as AccessTokenResponse;

  if (res.status === 200) {
    const { data } = res;
    const accessToken = data;

    const redirectUrl = is_white_label
      ? buildLoginRedirectUrl(
          buildLocalWhiteLabelSubdomainUrl(subdomain),
          accessToken
        )
      : null;

    if (redirectUrl) {
      await logout(); // logout the user before redirecting

      redirectToUrl(redirectUrl);
    }
  }
}
